/**
 * S3-Based Chat Store - Cloud Storage Version
 *
 * This replaces the localStorage-based chat-store.ts with S3 cloud storage
 * All data is stored on the server using WorkOS user authentication
 *
 * Key differences from old store:
 * - No localStorage persistence (data lives in S3)
 * - Sessions loaded from server on startup
 * - Messages saved to server in real-time
 * - Files uploaded to S3 and referenced by keys
 */

import { create } from 'zustand';
import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:store:s3-chat');
import { createJSONStorage, devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// Local state types (different from S3 storage types)
export type LocalChatSession = {
  id: string; // This is the sessionId from S3
  title: string;
  messages: LocalChatMessage[];
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  messageCount: number;
  isLoading?: boolean;
  hasUnsavedChanges?: boolean;
  isArchived: boolean; // Whether the chat is archived
  // Context compaction — populated after the user triggers context shrink
  compactionToken?: string;
  compactionMessageIndex?: number;
};

export type AttachmentMetadata = {
  s3Key: string;
  fileName: string;
  fileSizeMB: number;
  estimatedTokens: number;
  contentType: string;
};

export type LocalChatMessage = {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: string; // ISO string
  attachedFiles: string[]; // S3 file keys (legacy, kept for backward compatibility)
  attachmentMetadata?: AttachmentMetadata[]; // Enhanced metadata including token counts
  status?: 'sending' | 'sent' | 'error' | 'processing' | 'streaming';
  pageContext?: {
    title: string;
    url: string;
    charCount: number;
    wasTruncated: boolean;
  };
};

// Archive filter type
export type ArchiveFilter = 'active' | 'archived';

export type S3ChatState = {
  // Sessions loaded from S3 (most recent first)
  sessions: LocalChatSession[];
  // Currently active session
  currentSessionId: string | null;
  // Loading states
  isLoadingSessions: boolean;
  isCreatingSession: boolean;
  // Error states
  lastError: string | null;
  // Migration status
  migrationCompleted: boolean;
  // Archive filter (active or archived view)
  archiveFilter: ArchiveFilter;
};

export type S3ChatActions = {
  // Session Management (these will call the S3 API)
  loadSessions: () => Promise<void>;
  createSession: (title?: string) => Promise<string | null>;
  deleteSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  setCurrentSession: (sessionId: string | null) => void;

  // Archive management
  archiveSession: (sessionId: string) => void;
  unarchiveSession: (sessionId: string) => void;
  setArchiveFilter: (filter: ArchiveFilter) => void;

  // Message Management (these will call the S3 API)
  loadSessionMessages: (sessionId: string) => Promise<void>;

  // Local state management (for UI responsiveness)
  addOptimisticMessage: (sessionId: string, message: Omit<LocalChatMessage, 'id'>) => string;
  updateMessageStatus: (
    sessionId: string,
    messageId: string,
    status: LocalChatMessage['status']
  ) => void;
  updateMessageContent: (sessionId: string, messageId: string, content: string) => void;
  removeOptimisticMessage: (sessionId: string, messageId: string) => void;

  // Error handling
  setError: (error: string | null) => void;
  clearError: () => void;

  // Migration
  setMigrationCompleted: (completed: boolean) => void;

  // Utility
  reset: () => void;

  // Getters
  getSession: (sessionId: string) => LocalChatSession | null;
  getCurrentSession: () => LocalChatSession | null;
  getSessionList: () => LocalChatSession[];

  // Additional methods for S3 data synchronization
  setSessions: (sessions: LocalChatSession[]) => void;
  addSession: (session: LocalChatSession) => void;
  updateSession: (sessionId: string, updates: Partial<LocalChatSession>) => void;
  setSessionMessages: (sessionId: string, messages: LocalChatMessage[]) => void;
};

export type S3ChatStore = S3ChatState & S3ChatActions;

// Initial state
const initialState: S3ChatState = {
  sessions: [],
  currentSessionId: null,
  isLoadingSessions: false,
  isCreatingSession: false,
  lastError: null,
  migrationCompleted: false,
  archiveFilter: 'active',
};

// Create the store with persistence for user settings (data comes from S3)
export const useS3ChatStore = create<S3ChatStore>()(
  persist(
    subscribeWithSelector(
      immer(
        devtools(
          (set, get) =>
            ({
              ...initialState,

              // Session Management
              loadSessions: async () => {
                set(state => {
                  state.isLoadingSessions = true;
                  state.lastError = null;
                });

                try {
                  // This will be implemented by the component using React Query hooks
                  // The store just manages local state, API calls happen in components
                  throw new Error('loadSessions must be called from component with hooks');
                } catch (error) {
                  set(state => {
                    state.lastError =
                      error instanceof Error ? error.message : 'Failed to load sessions';
                    state.isLoadingSessions = false;
                  });
                }
              },

              createSession: async (title = `Chat ${new Date().toLocaleTimeString()}`) => {
                set(state => {
                  state.isCreatingSession = true;
                  state.lastError = null;
                });

                try {
                  // This will be implemented by the component using React Query hooks
                  throw new Error('createSession must be called from component with hooks');
                } catch (error) {
                  set(state => {
                    state.lastError =
                      error instanceof Error ? error.message : 'Failed to create session';
                    state.isCreatingSession = false;
                  });
                  return null;
                }
              },

              deleteSession: async (sessionId: string) => {
                try {
                  // Optimistically remove from local state
                  set(state => {
                    state.sessions = state.sessions.filter(s => s.id !== sessionId);
                    if (state.currentSessionId === sessionId) {
                      state.currentSessionId = null;
                    }
                  });

                  // API call will be handled by component
                  throw new Error('deleteSession must be called from component with hooks');
                } catch (error) {
                  // Revert optimistic update on error
                  logger.error('Failed to delete session:', error);
                  set(state => {
                    state.lastError =
                      error instanceof Error ? error.message : 'Failed to delete session';
                  });
                }
              },

              updateSessionTitle: async (sessionId: string, title: string) => {
                try {
                  // Optimistically update local state
                  set(state => {
                    const session = state.sessions.find(s => s.id === sessionId);
                    if (session) {
                      session.title = title;
                      session.updatedAt = new Date().toISOString();
                    }
                  });

                  // API call will be handled by component
                  throw new Error('updateSessionTitle must be called from component with hooks');
                } catch (error) {
                  logger.error('Failed to update session title:', error);
                  set(state => {
                    state.lastError =
                      error instanceof Error ? error.message : 'Failed to update title';
                  });
                }
              },

              setCurrentSession: (sessionId: string | null) => {
                set(state => {
                  state.currentSessionId = sessionId;
                });
              },

              // Archive management
              archiveSession: (sessionId: string) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    session.isArchived = true;
                    session.updatedAt = new Date().toISOString();
                  }
                  // If archiving the current session, clear selection
                  if (state.currentSessionId === sessionId) {
                    state.currentSessionId = null;
                  }
                });
              },

              unarchiveSession: (sessionId: string) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    session.isArchived = false;
                    session.updatedAt = new Date().toISOString();
                  }
                });
              },

              setArchiveFilter: (filter: ArchiveFilter) => {
                set(state => {
                  state.archiveFilter = filter;
                  // Clear current session when switching views
                  state.currentSessionId = null;
                });
              },

              // Message Management
              loadSessionMessages: async (sessionId: string) => {
                try {
                  // Mark session as loading
                  set(state => {
                    const session = state.sessions.find(s => s.id === sessionId);
                    if (session) {
                      session.isLoading = true;
                    }
                  });

                  // API call will be handled by component
                  throw new Error('loadSessionMessages must be called from component with hooks');
                } catch (error) {
                  set(state => {
                    const session = state.sessions.find(s => s.id === sessionId);
                    if (session) {
                      session.isLoading = false;
                    }
                    state.lastError =
                      error instanceof Error ? error.message : 'Failed to load messages';
                  });
                }
              },

              // Local state management for UI responsiveness
              addOptimisticMessage: (sessionId: string, message: Omit<LocalChatMessage, 'id'>) => {
                const messageId = randomUUID();

                set(state => {
                  const sessionIndex = state.sessions.findIndex(s => s.id === sessionId);
                  if (sessionIndex !== -1) {
                    const session = state.sessions[sessionIndex];
                    if (session) {
                      const fullMessage: LocalChatMessage = {
                        ...message,
                        id: messageId,
                        timestamp: new Date().toISOString(),
                      };
                      session.messages.push(fullMessage);
                      session.messageCount = session.messages.length;
                      session.updatedAt = new Date().toISOString();
                      session.hasUnsavedChanges = true;

                      // Re-sort: move session to front if not already there (most recent first)
                      if (sessionIndex !== 0) {
                        state.sessions.splice(sessionIndex, 1);
                        state.sessions.unshift(session);
                      }
                    }
                  }
                });

                return messageId;
              },

              updateMessageStatus: (
                sessionId: string,
                messageId: string,
                status: LocalChatMessage['status']
              ) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    const message = session.messages.find(m => m.id === messageId);
                    if (message) {
                      if (status === undefined) {
                        delete message.status;
                      } else {
                        message.status = status;
                      }
                      session.hasUnsavedChanges = true;
                    }
                  }
                });
              },

              updateMessageContent: (sessionId: string, messageId: string, content: string) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    const message = session.messages.find(m => m.id === messageId);
                    if (message) {
                      message.content = content;
                      session.hasUnsavedChanges = true;
                    }
                  }
                });
              },

              removeOptimisticMessage: (sessionId: string, messageId: string) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    session.messages = session.messages.filter(m => m.id !== messageId);
                    session.messageCount = session.messages.length;
                  }
                });
              },

              // Error handling
              setError: (error: string | null) => {
                set(state => {
                  state.lastError = error;
                });
              },

              clearError: () => {
                set(state => {
                  state.lastError = null;
                });
              },

              // Migration
              setMigrationCompleted: (completed: boolean) => {
                set(state => {
                  state.migrationCompleted = completed;
                });
              },

              // Utility
              reset: () => {
                set(initialState);
              },

              // Getters
              getSession: (sessionId: string) => {
                const state = get();
                return state.sessions.find(s => s.id === sessionId) || null;
              },

              getCurrentSession: () => {
                const state = get();
                if (!state.currentSessionId) return null;
                return state.sessions.find(s => s.id === state.currentSessionId) || null;
              },

              getSessionList: () => {
                const state = get();
                return state.sessions;
              },

              // Methods to sync with S3 data (called by components)
              setSessions: (sessions: LocalChatSession[]) => {
                set(state => {
                  state.sessions = sessions;
                  state.isLoadingSessions = false;
                });
              },

              addSession: (session: LocalChatSession) => {
                set(state => {
                  // Add to beginning (most recent first)
                  state.sessions.unshift(session);
                  state.isCreatingSession = false;
                });
              },

              updateSession: (sessionId: string, updates: Partial<LocalChatSession>) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    Object.assign(session, updates);
                    session.hasUnsavedChanges = false;
                  }
                });
              },

              setSessionMessages: (sessionId: string, messages: LocalChatMessage[]) => {
                set(state => {
                  const session = state.sessions.find(s => s.id === sessionId);
                  if (session) {
                    session.messages = messages;
                    session.messageCount = messages.length;
                    session.isLoading = false;
                    session.hasUnsavedChanges = false;
                  }
                });
              },
            }) as S3ChatActions, // Type assertion needed for additional methods
          { name: 'S3ChatStore' }
        )
      )
    ),
    {
      name: 's3-chat-store',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        // Only persist user preferences - sessions come from S3
        migrationCompleted: state.migrationCompleted,
        // Don't persist: sessions, currentSessionId, loading states, errors
      }),
      version: 2, // Incremented version to handle migration
      migrate: (persistedState: unknown, version: number) => {
        // Handle migration from older versions
        if (!version || version < 2) {
          // Remove obsolete defaultPrompt field (replaced by personalization custom instructions)
          return {
            migrationCompleted: false,
          };
        }
        return persistedState;
      },
    }
  )
);

// Selectors for common use cases
export const useCurrentSession = () =>
  useS3ChatStore(state => {
    if (!state.currentSessionId) return null;
    return state.sessions.find(s => s.id === state.currentSessionId) || null;
  });

export const useSessionById = (sessionId: string | null) =>
  useS3ChatStore(state => {
    if (!sessionId) return null;
    return state.sessions.find(s => s.id === sessionId) || null;
  });

export const useSessionList = () => useS3ChatStore(state => state.sessions);

export const useIsLoading = () =>
  useS3ChatStore(state => state.isLoadingSessions || state.isCreatingSession);

export const useLastError = () => useS3ChatStore(state => state.lastError);

// Archive-related selectors
export const useArchiveFilter = () => useS3ChatStore(state => state.archiveFilter);

export const useFilteredSessions = () =>
  useS3ChatStore(state => {
    const filter = state.archiveFilter;
    return state.sessions.filter(s => (filter === 'archived' ? s.isArchived : !s.isArchived));
  });

export const useActiveSessions = () =>
  useS3ChatStore(state => state.sessions.filter(s => !s.isArchived));

export const useArchivedSessions = () =>
  useS3ChatStore(state => state.sessions.filter(s => s.isArchived));

export const useArchivedCount = () =>
  useS3ChatStore(state => state.sessions.filter(s => s.isArchived).length);
